import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Check, CheckCheck, X, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const NOTIFICATION_ICONS = {
  info: '📢',
  success: '✅',
  warning: '⚠️',
  error: '❌',
  candidatura: '📋',
  visita: '📅',
  documento: '📄',
  mensagem: '💬'
};

const NOTIFICATION_COLORS = {
  info: 'bg-blue-50 border-blue-200',
  success: 'bg-green-50 border-green-200',
  warning: 'bg-amber-50 border-amber-200',
  error: 'bg-red-50 border-red-200',
  candidatura: 'bg-purple-50 border-purple-200',
  visita: 'bg-cyan-50 border-cyan-200',
  documento: 'bg-slate-50 border-slate-200',
  mensagem: 'bg-indigo-50 border-indigo-200'
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const connectSSE = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const apiUrl = process.env.REACT_APP_BACKEND_URL;
    const url = `${apiUrl}/api/sse/notifications`;
    
    // Using fetch with ReadableStream for SSE with auth header
    const controller = new AbortController();
    
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'text/event-stream',
      },
      signal: controller.signal
    }).then(async response => {
      if (!response.ok) {
        throw new Error('SSE connection failed');
      }
      
      setConnected(true);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            eventData = line.slice(5).trim();
          } else if (line === '' && eventType && eventData) {
            // Process complete event
            try {
              const data = JSON.parse(eventData);
              handleSSEEvent(eventType, data);
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
            eventType = '';
            eventData = '';
          }
        }
      }
    }).catch(error => {
      if (error.name !== 'AbortError') {
        console.warn('SSE not available, using polling fallback');
        setConnected(false);
        // Fallback to polling - don't retry SSE
        startPolling();
      }
    });

    eventSourceRef.current = { close: () => controller.abort() };
  }, []);

  // Polling fallback when SSE is not available (K8s ingress doesn't support streaming)
  const pollingIntervalRef = useRef(null);
  
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) return; // Already polling
    
    console.log('Starting notification polling fallback');
    // Poll every 30 seconds
    pollingIntervalRef.current = setInterval(() => {
      loadNotifications();
      loadUnreadCount();
    }, 30000);
  }, []);

  const handleSSEEvent = (type, data) => {
    switch (type) {
      case 'connected':
        console.log('SSE Connected:', data.connection_id);
        break;
      case 'unread_count':
        setUnreadCount(data.count);
        break;
      case 'notification':
        // Add new notification to the top
        setNotifications(prev => [data, ...prev].slice(0, 50));
        setUnreadCount(prev => prev + 1);
        // Show toast for new notification
        toast(data.titulo, {
          description: data.mensagem,
          action: data.link ? {
            label: 'Ver',
            onClick: () => navigate(data.link)
          } : undefined
        });
        break;
      case 'ping':
        // Keepalive, no action needed
        break;
      default:
        console.log('Unknown SSE event:', type, data);
    }
  };

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sse/notificacoes?limit=20`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        // Support both paginated response and direct array
        const items = Array.isArray(data) ? data : (data.items || []);
        setNotifications(items);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sse/notificacoes/contagem`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sse/notificacoes/${notificationId}/lida`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, lida: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/sse/notificacoes/marcar-todas-lidas`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, lida: true })));
        setUnreadCount(0);
        toast.success('Todas as notificações marcadas como lidas');
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.lida) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadNotifications();
      loadUnreadCount();
      connectSSE();
    }

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [connectSSE]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString('pt-PT');
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-white text-xs"
              data-testid="notification-badge"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          {connected && (
            <span className="absolute bottom-0 right-0 h-2 w-2 bg-green-500 rounded-full border border-white" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold text-sm">Notificações</span>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs"
              onClick={markAllAsRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Marcar todas
            </Button>
          )}
        </div>
        <ScrollArea className="h-[350px]">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-slate-500">
              <Bell className="h-10 w-10 mb-2 opacity-50" />
              <span className="text-sm">Sem notificações</span>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={`px-3 py-2 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                  !notification.lida ? NOTIFICATION_COLORS[notification.tipo] || 'bg-blue-50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
                data-testid={`notification-item-${notification.id}`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-lg">
                    {NOTIFICATION_ICONS[notification.tipo] || '📢'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-medium truncate ${!notification.lida ? 'text-slate-900' : 'text-slate-600'}`}>
                        {notification.titulo}
                      </span>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {formatTime(notification.created_at)}
                      </span>
                    </div>
                    <p className={`text-xs mt-0.5 line-clamp-2 ${!notification.lida ? 'text-slate-700' : 'text-slate-500'}`}>
                      {notification.mensagem}
                    </p>
                    {notification.link && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-[#0066CC]">
                        <ExternalLink className="h-3 w-3" />
                        Ver detalhes
                      </div>
                    )}
                  </div>
                  {!notification.lida && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </ScrollArea>
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-3 py-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => {
                  navigate('/notificacoes');
                  setIsOpen(false);
                }}
              >
                Ver todas as notificações
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
